import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { getExtension } from '../../extension-details/state/extension-detail.selectors';
import { CallParkingLotFilterOptions } from '../models/call-parking-filter-option';
import { CallParkingRegisterOptions } from '../models/call-parkinglot-register-options';
import {
   CallParkingLotRegisterListItem,
   CallParkingLotRegisterResults,
} from '../models/call-parkinglot-register-results.model';
import {
   GetCallParkingLots,
   GetCallParkingLots_callParkingLotsConnection_items,
} from '../queries/generated/GetCallParkingLots';
import { CallParkingLotRegisterApiService } from '../services/call-parkinglot-register-api.service';
import { callParkingLotRegisterAdapterActions } from './call-parkinglot-register.actions';
import { CallParkingLotRegisterState, selectEndCursor, selectQuickFilters } from './call-parkinglot-register.reducer';
import * as CallLogsRegisterSelectors from './call-parkinglot-register.selectors';
import { selectFilters } from './call-parkinglot-register.selectors';

@Injectable()
export class CallLogsRegisterEffects {
   loadCallLogsList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callParkingLotRegisterAdapterActions.pageEnter),
         withLatestFrom(this.store.pipe(select(selectFilters)), this.store.pipe(select(getExtension))),
         switchMap(([action, filters]) => {
            return this.callLogsRegisterApiService.getParkingLots(null, filters, null, null).pipe(
               map((result) => {
                  return callParkingLotRegisterAdapterActions.loadSuccess(
                     this.convertGetCallParkingLotResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(callParkingLotRegisterAdapterActions.loadFailure(error));
               })
            );
         })
      );
   });

   loadCallLogsPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callParkingLotRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters))
         ),

         switchMap(([action, endCursor, quickFilters, filters]) => {
            return this.callLogsRegisterApiService.getParkingLots(endCursor, filters, quickFilters, null).pipe(
               map((result) => {
                  return callParkingLotRegisterAdapterActions.loadPageSuccess(
                     this.convertGetCallParkingLotResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(callParkingLotRegisterAdapterActions.loadPageFailure(error));
               })
            );
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callParkingLotRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(CallLogsRegisterSelectors.selectFilters))
         ),
         switchMap(([action, quickFilters, filters]) => {
            return this.loadNewData(filters, quickFilters);
         })
      );
   });

   constructor(
      private actions$: Actions,
      private store: Store<CallParkingLotRegisterState>,
      private callLogsRegisterApiService: CallParkingLotRegisterApiService,
      private dialog: MatDialog
   ) {}

   private loadNewData(filters: CallParkingLotFilterOptions[], quickFilters: CallParkingRegisterOptions | null = null) {
      return this.callLogsRegisterApiService.getParkingLots(null, filters, quickFilters, null).pipe(
         map((result) => {
            return callParkingLotRegisterAdapterActions.loadSuccess(
               this.convertGetCallParkingLotResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(callParkingLotRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(endCursor: string | null, filters: any = null) {
      return this.callLogsRegisterApiService.getParkingLots(endCursor, filters, null, null).pipe(
         map((result) => {
            return callParkingLotRegisterAdapterActions.loadPageSuccess(
               this.convertGetCallParkingLotResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(callParkingLotRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetCallParkingLotResultToLoadSuccessProps(
      getCallLogs: GetCallParkingLots
   ): CallParkingLotRegisterResults {
      return {
         endCursor: getCallLogs.callParkingLotsConnection.pageInfo.endCursor,
         hasNextPage: getCallLogs.callParkingLotsConnection.pageInfo.hasNextPage,
         items: getCallLogs.callParkingLotsConnection.items.map(this.convertCallParkingLotRegisterListItem),
         totalCount: getCallLogs.callParkingLotsConnection.totalCount,
      };
   }

   private convertCallParkingLotRegisterListItem(
      getCallLogsItem: GetCallParkingLots_callParkingLotsConnection_items
   ): CallParkingLotRegisterListItem {
      return {
         parkerExtension: getCallLogsItem.parkerExtension,
         callerIdInformation: getCallLogsItem.callerIdInformation ? getCallLogsItem.callerIdInformation : '',
         parkingSlotNumber: getCallLogsItem.parkingSlotNumber,
         parkDateTime: getCallLogsItem.parkDateTime,
      };
   }
}
